import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { DateService } from '@app-core/services/date/date.service';
import { BREAKPOINTS_MEDIUM, BREAKPOINTS_SMALL, CSV_MIME_TYPE, MAX_DATE_RANGE_IN_MONTHS } from '@app-core/constants/constants';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { TripsService } from '@app-trips/services/trips.service';
import { finalize, skip, takeUntil } from 'rxjs/operators';
import { Observable, Subject, of } from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import { DataService } from '@app-core/services/data/data.service';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import {
  PreviewUpdateDriverMultipleTripsParams,
  PreviewUpdateDriverMultipleTripsResponse,
  UpdateDriverMultipleTripsBody,
  updationType,
} from '@app-trips/common/trips.model';
import { CanComponentDeactivate } from '@app-core/guards/can-deactivate/can-deactivate.guard';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmDialogComponent } from '@app-shared/components/dialog/confirm-dialog/confirm-dialog.component';
import { AssetAutoCompleteResponseObject } from '@app-core/models/core.model';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';
@Component({
  selector: 'app-bulk-edit-trip',
  templateUrl: './bulk-edit-trip.component.html',
  styleUrls: ['./bulk-edit-trip.component.scss'],
})
export class BulkEditTripComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  @ViewChild('stepper') public stepper: MatStepper;
  @ViewChild('fileInputRef') public fileInputRef: ElementRef;

  private fleetId: string;
  public currentTheme = 'light';
  public form: FormGroup;
  public tripDetailsForm: FormGroup;
  public updationTypeEnum = updationType;
  public maxStartDate: string;
  public maxEndDate: string;
  public minStartDate: string;
  public isTouchDevice = false;
  public isLinear: boolean = true;
  private ALL_DRIVERS_ID = 'All Drivers';

  public previewUpdateDriverLoader: boolean = false;
  public updateDriverLoader: boolean = false;
  public downloadSampleCSVLoader: boolean = false;
  public sampleDownloadError: boolean = false;
  public uploadCSVloader: boolean = false;
  public driverUpdationCSVFile;
  public csvFileSelected: boolean = false;
  public showFileError: boolean = false;
  public tripsAffected: number;
  private previousTripDetails: string;

  private ngUnsubscribe: Subject<void> = new Subject<void>();
  public canDeactivateFlag: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private dateService: DateService,
    private dataService: DataService,
    private tripsService: TripsService,
    private breakpointObserver: BreakpointObserver,
    private location: Location,
    private dialog: MatDialog,
    private snackBarService: SnackBarService,
    private translate: TranslateService,
    private mixpanelService: MixPanelService
  ) {}

  ngOnInit(): void {
    this.tripDetailsForm = this.formBuilder.group({
      startDate: ['', Validators.required],
      endDate: ['', Validators.required],
      oldDriverId: [this.ALL_DRIVERS_ID, Validators.required],
      asset: [null, Validators.required],
    });
    this.form = this.formBuilder.group({
      updationType: [this.updationTypeEnum.singleDriver, Validators.required],
      tripDetails: this.tripDetailsForm,
      newDriverId: ['', Validators.required],
    });

    this.breakpointObserver
      .observe([...BREAKPOINTS_SMALL, ...BREAKPOINTS_MEDIUM])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((state: BreakpointState) => {
        this.isTouchDevice = state.matches;
      });

    this.dataService._currentFleet.pipe(skip(1), takeUntil(this.ngUnsubscribe)).subscribe((value) => {
      if (value) {
        this.fleetId = value;
        this.navigateBack();
      }
    });

    this.dataService._currentTheme.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value) {
        this.currentTheme = value;
      }
    });

    this.setMinMaxDateRanges();
    this.subscribeToFormChanges();
  }

  get updationTypeValue() {
    return this.form.get('updationType').value;
  }

  public onStartDateChange() {
    const { startDate, endDate } = this.tripDetailsForm.value;
    if (startDate && endDate && startDate.getTime() > endDate.getTime()) {
      this.tripDetailsForm.patchValue({
        endDate: null,
      });
    }
  }

  private setMinMaxDateRanges() {
    this.maxStartDate = this.maxEndDate = new Date().toISOString();
    this.minStartDate = this.dateService.subtractMonths(this.maxStartDate, MAX_DATE_RANGE_IN_MONTHS);
  }

  private subscribeToFormChanges() {
    this.tripDetailsForm.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.tripsAffected = undefined;
    });
  }

  public oldDriverChanged(driverId = '') {
    this.tripDetailsForm.patchValue({ oldDriverId: driverId });
  }

  public assetChanged(asset: AssetAutoCompleteResponseObject) {
    this.tripDetailsForm.patchValue({ asset });
  }

  public newDriverChanged(driverId = '') {
    this.form.patchValue({ newDriverId: driverId });
  }

  public getDisplayDateString(date: string): string {
    if (date.length > 0) {
      const arr = date.split(' ');
      return arr[0];
    }
    return ' ';
  }

  public navigateBack(canDeactivate?: boolean) {
    if (canDeactivate) {
      this.canDeactivateFlag = true;
    }
    this.location.back();
  }

  public selectCSVFile(event: any) {
    this.driverUpdationCSVFile = event.target.files?.[0];
    this.showFileError = false;
    this.csvFileSelected = false;

    if (!this.driverUpdationCSVFile) {
      return;
    }

    if (!CSV_MIME_TYPE.includes(this.driverUpdationCSVFile.type)) {
      this.showFileError = true;
      return;
    }

    this.csvFileSelected = true;
  }

  public resetUpload() {
    this.uploadCSVloader = false;
    this.csvFileSelected = false;
    this.driverUpdationCSVFile = {};
    if (this.fileInputRef) {
      this.fileInputRef.nativeElement.value = '';
    }
  }

  public downloadSampleBulkAssetTripUpdationCSV() {
    this.downloadSampleCSVLoader = true;
    this.sampleDownloadError = false;

    this.tripsService
      .getSampleBulkAssetTripUpdationCSV()
      .pipe(
        takeUntil(this.ngUnsubscribe),
        finalize(() => {
          this.downloadSampleCSVLoader = false;
        })
      )
      .subscribe(
        (res: HttpResponse<Blob>) => {
          const defaultFilename = `${this.fleetId}_bulkAssetTripDriverUpdationSample.csv`;
          const contentDispositionHeader = res.headers.get('content-disposition');
          const fileName = contentDispositionHeader ? this.tripsService.extractFilename(contentDispositionHeader) : defaultFilename;
          this.tripsService.downloadFile(res.body, fileName, res.headers.get('content-type'));
        },
        () => {
          this.sampleDownloadError = true;
        }
      );
  }

  public bulkAssetTripDriverUpdation() {
    if (!this.driverUpdationCSVFile) {
      return;
    }

    this.uploadCSVloader = true;
    this.tripsService
      .uploadBulkAssetTripUpdationCSV(this.driverUpdationCSVFile)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        finalize(() => {
          this.uploadCSVloader = false;
          this.resetUpload();
        })
      )
      .subscribe(
        () => {
          this.snackBarService.success(this.translate.instant('bulkEditTripCSVUploadSuccess'));
          this.navigateBack(true);
        },
        () => {
          this.snackBarService.failure(this.translate.instant('bulkEditTripCSVUploadFailed'));
        }
      );
  }

  public previewUpdateDriverMultipleTrips() {
    if (!this.tripDetailsForm.valid) {
      return;
    }

    const currentTripDetails = JSON.stringify(this.tripDetailsForm.value);

    if (this.previousTripDetails) {
      const hasChanged: boolean = currentTripDetails !== this.previousTripDetails;
      if (!hasChanged && this.tripsAffected !== undefined) {
        this.stepper.next();
        return;
      }
    }

    this.tripsAffected = undefined;
    const { startDate, endDate, asset, oldDriverId } = this.tripDetailsForm.value;
    const from = this.dateService.toDaysStartISO(startDate);
    const to = this.dateService.toDaysEndISO(endDate);

    const params: PreviewUpdateDriverMultipleTripsParams = {
      startDate: from,
      endDate: to,
      ...(oldDriverId && oldDriverId !== this.ALL_DRIVERS_ID ? { oldDriverId: oldDriverId } : {}),
    };

    this.previewUpdateDriverLoader = true;
    this.stepper.next();

    this.tripsService
      .previewUpdateDriverMultipleTrips(asset?.assetId, params)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        finalize(() => {
          this.previewUpdateDriverLoader = false;
        })
      )
      .subscribe(
        (res: PreviewUpdateDriverMultipleTripsResponse) => {
          this.tripsAffected = res.updatedTripIds?.length;
          this.previousTripDetails = currentTripDetails;
        },
        () => {
          this.tripsAffected = undefined;
        }
      );
  }

  public onSaveDetails() {
    if (!this.form.valid) {
      return;
    }

    const { newDriverId } = this.form.value;
    const { startDate, endDate, asset, oldDriverId } = this.tripDetailsForm.value;
    const from = this.dateService.toDaysStartISO(startDate);
    const to = this.dateService.toDaysEndISO(endDate);

    const body: UpdateDriverMultipleTripsBody = {
      newDriverId: newDriverId,
      startDate: from,
      endDate: to,
      ...(oldDriverId && oldDriverId !== this.ALL_DRIVERS_ID ? { oldDriverId: oldDriverId } : {}),
    };

    this.mixpanelService.track('Save Detils - Bulk Edit', {
      'Old Driver Id': oldDriverId || 'None',
      'New Driver Id': newDriverId,
      'Start Date': from,
      'End Date': to,
      'Asset Id': asset?.assetId,
    });
    this.updateDriverLoader = true;
    this.tripsService
      .updateDriverMultipleTrips(asset?.assetId, body)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        finalize(() => {
          this.updateDriverLoader = false;
        })
      )
      .subscribe(
        () => {
          this.snackBarService.success(this.translate.instant('bulkEditTripUpdateDriverSuccess'));
          this.navigateBack(true);
        },
        () => {
          this.snackBarService.failure(this.translate.instant('bulkEditTripUpdateDriverFailed'));
        }
      );
  }

  public canDeactivate(): Observable<boolean> {
    if ((this.tripDetailsForm.touched || this.csvFileSelected) && !this.canDeactivateFlag) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        panelClass: [this.currentTheme === 'dark' ? 'dark-theme' : '', 'mobile-modal'],
        position: { top: '24px' },
        minHeight: '200px',
        data: {
          title: this.translate.instant('bulkEditTripDiscardDialog'),
          content: this.translate.instant('bulkEditTripDiscardDialogMessage'),
          actions: [
            { actionButton: this.translate.instant('bulkEditTripDiscardDialogCancel'), dialogCloseValue: false },
            { actionButton: this.translate.instant('bulkEditTripDiscardDialogConfirm'), dialogCloseValue: true, primary: true },
          ],
        },
      });
      return dialogRef.afterClosed();
    }
    return of(true);
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
